import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User, LoginCredentials, AuthSession } from "@/types/auth";
import { kvService } from "@/lib/kv-service";
import { generateUUID } from "@/lib/uuid";

// Always use Cloudflare KV for authentication
const authService = kvService;

// Simple password verification (in production, use proper hashing like bcrypt)
async function verifyPassword(username: string, password: string): Promise<boolean> {
  try {
    // Get user data which should include password
    const response = await fetch(`${import.meta.env.VITE_KV_API_URL}/auth/verify`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_KV_TOKEN}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });
    
    if (!response.ok) {
      return false;
    }
    
    const result = await response.json();
    return result.valid === true;
  } catch (error) {
    console.error('Password verification failed:', error);
    return false;
  }
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem("auth-token");
      if (!token) {
        setIsLoading(false);
        return;
      }

      const session = await authService.getSession(token);
      if (session && new Date() < session.expiresAt) {
        setUser(session.user);
      } else {
        localStorage.removeItem("auth-token");
        if (session) {
          await authService.deleteSession(token);
        }
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem("auth-token");
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Get user from service
      const user = await authService.getUser(credentials.username);
      if (!user) {
        return false;
      }

      // In a real app, you'd verify the password hash here
      // For now, we'll use a simple check with the stored user data
      // TODO: Implement proper password hashing (bcrypt, etc.)
      const validPassword = await verifyPassword(credentials.username, credentials.password);
      
      if (!validPassword) {
        return false;
      }

      // Create session
      const token = generateUUID();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // 7 days

      const session: AuthSession = {
        user,
        token,
        expiresAt,
      };

      await authService.createSession(session);
      await authService.updateUserLastLogin(credentials.username);

      localStorage.setItem("auth-token", token);
      setUser(user);
      
      return true;
    } catch (error) {
      console.error("Login failed:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("auth-token");
      if (token) {
        await authService.deleteSession(token);
        localStorage.removeItem("auth-token");
      }
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}